<template>
  <div class="pw-profile">
    <v-row class="ma-0 my-4 align-center">
      <div>
        <h1 class="text-h5 font-weight-bold">Mi perfil</h1>
        <p v-if="user" class="ma-0 text-body-2">
          {{ user.name }}<span v-if="user.email"> · {{ user.email }}</span>
        </p>
      </div>
    </v-row>

    <v-row>
      <v-col cols="12" md="8" lg="6">
        <v-card outlined :loading="loading">
          <v-card-title class="text-subtitle-1 font-weight-bold">Sección de inicio</v-card-title>
          <v-card-text>
            <p class="pw-label mb-3">
              La vista que se abre al entrar al admin. Los enlaces directos no se ven afectados.
            </p>

            <v-alert v-if="unavailable" type="warning" text dense class="mb-4">
              La sección que tenías guardada ya no está disponible. Mientras tanto entras a
              {{ defaultAllowed ? 'MRR Resumen' : 'la primera sección disponible' }}.
            </v-alert>

            <v-autocomplete
              v-model="selectedKey"
              :items="sectionItems"
              :disabled="loading || saving"
              label="Sección de inicio"
              prepend-inner-icon="mdi-home-outline"
              outlined
              dense
              hide-details
            ></v-autocomplete>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn color="primary" :loading="saving" :disabled="loading || !hasChanges" @click="save">
              Guardar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="4000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import AuthService from '../services/auth.service';
import { utilsMixin } from '../mixins/utilsMixin';
import {
  DEFAULT_SECTION_KEY,
  getSelectableSections,
  isSectionAllowed
} from '../utils/nav-sections.utils';
import { loadPermissions } from '../utils/permissions';

export default {
  name: 'Profile',
  mixins: [utilsMixin],
  data() {
    return {
      loading: false,
      saving: false,
      user: null,
      // Key stored in the backend, even when it is no longer selectable.
      savedKey: null,
      // Sections this admin may open, filled after /me (spec 131).
      allowedSections: [],
      selectedKey: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'green'
    };
  },
  computed: {
    sectionItems() {
      // Only the sections this admin may open (spec 131). "Predeterminada" lands
      // on MRR Resumen, so it is offered only when that section is allowed.
      const items = this.defaultAllowed
        ? [{ text: 'Predeterminada (MRR Resumen)', value: null }]
        : [];
      let group = null;
      this.allowedSections.forEach((section) => {
        if (section.group !== group) {
          group = section.group;
          items.push({ header: group });
        }
        items.push({ text: section.title, value: section.key });
      });
      return items;
    },
    unavailable() {
      return Boolean(this.savedKey) && !this.isAllowedKey(this.savedKey);
    },
    defaultAllowed() {
      return this.isAllowedKey(DEFAULT_SECTION_KEY);
    },
    hasChanges() {
      return this.selectedKey !== this.savedKey;
    }
  },
  async created() {
    this.loading = true;
    // Fresh /me: the preference may have changed since the cached one, and the
    // refreshed cache keeps the menu and the guard in sync.
    const res = await loadPermissions({ refresh: true });
    if (res.status === 'ok') {
      this.user = res.data;
      this.allowedSections = getSelectableSections(isSectionAllowed);
      this.savedKey = (res.data.preferences && res.data.preferences.defaultSection) || null;
      this.selectedKey = this.selectableOrNull(this.savedKey);
    } else {
      this.showErrorSnackbar('No se pudo cargar tu perfil');
    }
    this.loading = false;
  },
  methods: {
    selectableOrNull(key) {
      return this.isAllowedKey(key) ? key : null;
    },
    isAllowedKey(key) {
      return this.allowedSections.some((section) => section.key === key);
    },
    async save() {
      this.saving = true;
      const res = await AuthService.updatePreferences({ defaultSection: this.selectedKey });
      if (res.status === 'ok') {
        const preferences = (res.data && res.data.preferences) || {};
        this.savedKey = preferences.defaultSection || null;
        this.showSuccessSnackbar('Sección de inicio guardada');
      } else {
        this.selectedKey = this.selectableOrNull(this.savedKey);
        this.showErrorSnackbar('No se pudo guardar la sección de inicio');
      }
      this.saving = false;
    }
  }
};
</script>

<style scoped>
.pw-profile {
  max-width: 1200px;
}
</style>
